import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildAuthenticatedHeaders } from './authStorage.ts';
import type {
  AppSettingsRecord,
  AppUserRecord,
  UpdateAppSettingsInput,
} from './apiContract.ts';
export type {
  AppBillingView,
  AppDataModePreference,
  AppLandingRoute,
  AppPreferencesRecord,
  AppSettingsRecord,
  AppUserRecord,
  UpdateAppSettingsInput,
} from './apiContract.ts';

export const SETTINGS_QUERY_KEY_ROOT = ['field-service-technique-settings'];

function getSettingsApiBaseUrl(): string {
  const raw = String(import.meta.env.VITE_DEMO_API_BASE_URL || '/api').trim();
  if (!raw) return '/api';
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

async function requestSettingsJson<T>(path: string, init?: RequestInit, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${getSettingsApiBaseUrl()}${path}`, {
    ...init,
    signal,
    credentials: 'include',
    headers: buildAuthenticatedHeaders({
      Accept: 'application/json',
      ...(init?.headers || {}),
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    let detail = response.statusText;
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { detail?: string; error?: string };
      detail = payload.detail || payload.error || detail;
    } else {
      detail = (await response.text()) || detail;
    }
    throw new Error(`HTTP ${response.status}: ${detail}`);
  }

  if (!contentType.includes('application/json')) {
    throw new Error(`Type de contenu inattendu renvoyé par l’API de paramètres : ${contentType || 'inconnu'}`);
  }

  return (await response.json()) as T;
}

export async function fetchAppUsers(signal?: AbortSignal): Promise<AppUserRecord[]> {
  return requestSettingsJson<AppUserRecord[]>('/app/users', undefined, signal);
}

export async function fetchAppSettings(signal?: AbortSignal): Promise<AppSettingsRecord> {
  return requestSettingsJson<AppSettingsRecord>('/app/settings', undefined, signal);
}

export async function updateAppSettings(payload: UpdateAppSettingsInput): Promise<AppSettingsRecord> {
  return requestSettingsJson<AppSettingsRecord>(
    '/app/settings',
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
    undefined,
  );
}

export function useAppUsersData(enabled: boolean) {
  const usersQuery = useQuery({
    queryKey: [...SETTINGS_QUERY_KEY_ROOT, 'users'],
    queryFn: ({ signal }) => fetchAppUsers(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    users: (usersQuery.data as AppUserRecord[] | null | undefined) ?? [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error,
  };
}

export function useAppSettingsData(enabled: boolean) {
  const settingsQuery = useQuery({
    queryKey: [...SETTINGS_QUERY_KEY_ROOT, 'current'],
    queryFn: ({ signal }) => fetchAppSettings(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  return {
    settings: (settingsQuery.data as AppSettingsRecord | null | undefined) ?? null,
    isLoading: settingsQuery.isLoading,
    error: settingsQuery.error,
  };
}

export function useUpdateAppSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateAppSettingsInput) => updateAppSettings(payload),
    onSuccess: async (settings) => {
      queryClient.setQueryData([...SETTINGS_QUERY_KEY_ROOT, 'current'], settings);
      await queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY_ROOT });
    },
  });
}
